import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import type { ThemeFamily } from "@/lib/api/types";
import { THEME_FAMILIES, THEME_NICHES } from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { getRequiredFieldErrors } from "@/shared/components/forms/validation";
import { Banner, Button, Drawer, Field, FilterChip, Input, Select, TextArea, useToast } from "@/shared/rd";
import { useCreateThemeMutation } from "../services/useCreateThemeMutation";

const FAMILY_LABEL_KEY: Record<ThemeFamily, string> = {
  starter: keys.themes.families.starter,
};

const FORM_ID = "theme-create-form";

/**
 * "Create theme", in a drawer over the themes list. Its own route
 * (`themes/new`) opens it, so the URL still deep-links to the form; closing
 * it goes back to the list and a successful create lands on the new theme.
 */
export function ThemeCreateForm({ locale, onClose }: { locale: Locale; onClose: () => void }) {
  const navigate = useNavigate();
  const t = useTranslations();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [family, setFamily] = useState<ThemeFamily>("starter");
  const [niches, setNiches] = useState<string[]>([]);
  const { mutateAsync: createTheme } = useCreateThemeMutation();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;
    setError(null);
    setFieldErrors({});
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    const requiredErrors = getRequiredFieldErrors(formData, ["slug", "nameAr", "nameEn"], "");
    if (Object.keys(requiredErrors).length > 0) {
      // Each says how to fix its own field.
      const howToFix: Record<string, string> = {
        slug: t("themes.slugRequired"),
        nameAr: t("themes.nameArRequired"),
        nameEn: t("themes.nameEnRequired"),
      };
      setFieldErrors(Object.fromEntries(Object.keys(requiredErrors).map((field) => [field, howToFix[field] ?? t(keys.errors.required)])));
      setIsSubmitting(false);
      return;
    }

    const sortWeightRaw = String(formData.get("sortWeight") ?? "").trim();
    const sortWeight = sortWeightRaw.length > 0 ? Number(sortWeightRaw) : undefined;
    if (sortWeight !== undefined && !Number.isFinite(sortWeight)) {
      setFieldErrors({ sortWeight: t("themes.sortWeightNumber") });
      setIsSubmitting(false);
      return;
    }

    const descriptionAr = String(formData.get("descriptionAr") ?? "").trim();
    const descriptionEn = String(formData.get("descriptionEn") ?? "").trim();

    try {
      const theme = await createTheme({
        family,
        slug: String(formData.get("slug") ?? "").trim(),
        nameAr: String(formData.get("nameAr") ?? "").trim(),
        nameEn: String(formData.get("nameEn") ?? "").trim(),
        descriptionAr: descriptionAr.length > 0 ? descriptionAr : undefined,
        descriptionEn: descriptionEn.length > 0 ? descriptionEn : undefined,
        niches: niches.length > 0 ? niches : undefined,
        sortWeight,
      });
      // The toast lives in the shell, so it survives the navigation.
      toast.show({ message: t("themes.createdToast", { name: theme.nameEn || theme.nameAr }) });
      navigate(`/${locale}/admin/themes/${theme.id}`, { replace: true });
    } catch (submitError) {
      setError(submitError instanceof ApiJsonClientError ? submitError.message || "" : "");
      setIsSubmitting(false);
    }
  };

  const toggleNiche = (value: string) =>
    setNiches((current) => (current.includes(value) ? current.filter((n) => n !== value) : [...current, value]));

  return (
    <Drawer
      open
      onClose={onClose}
      closeLabel={t(keys.common.close)}
      crumb={`${t(keys.themes.title)} / ${t("themes.newCrumb")}`}
      badge="note"
      title={t(keys.themes.newTitle)}
      sub={t("themes.createSub")}
      width={560}
      actions={
        <Button
          type="submit"
          form={FORM_ID}
          variant="primary"
          size="sm"
          loading={isSubmitting}
          loadingLabel={t("themes.creating")}
        >
          {t(keys.common.create)}
        </Button>
      }
    >
      <form id={FORM_ID} noValidate onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {error !== null ? (
          <Banner
            tone="error"
            title={t("themes.createFailedTitle")}
            actions={
              <Button type="submit" size="sm" loading={isSubmitting} loadingLabel={t(keys.shell.retrying)}>
                {t(keys.shell.retry)}
              </Button>
            }
          >
            {[error, t("themes.nothingChanged")].filter(Boolean).join(" ")}
          </Banner>
        ) : null}

        <Field label={t(keys.themes.family)} htmlFor="theme-family" error={fieldErrors.family}>
          <Select
            id="theme-family"
            value={family}
            onChange={(event) => setFamily((event.target.value as ThemeFamily) || "starter")}
            options={THEME_FAMILIES.map((value) => ({ value, label: t(FAMILY_LABEL_KEY[value]) }))}
            style={{ maxWidth: 280 }}
          />
        </Field>

        <Field label={t(keys.themes.slug)} htmlFor="theme-slug" error={fieldErrors.slug}>
          <Input
            id="theme-slug"
            name="slug"
            dir="ltr"
            placeholder={t(keys.themes.slugPlaceholder)}
            invalid={Boolean(fieldErrors.slug)}
          />
        </Field>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))", gap: 12 }}>
          <Field label={t(keys.themes.nameAr)} htmlFor="theme-name-ar" error={fieldErrors.nameAr}>
            <Input id="theme-name-ar" name="nameAr" dir="rtl" invalid={Boolean(fieldErrors.nameAr)} />
          </Field>
          <Field label={t(keys.themes.nameEn)} htmlFor="theme-name-en" error={fieldErrors.nameEn}>
            <Input id="theme-name-en" name="nameEn" dir="ltr" invalid={Boolean(fieldErrors.nameEn)} />
          </Field>
          <Field label={t("themes.descriptionArOptional")} htmlFor="theme-desc-ar">
            <TextArea id="theme-desc-ar" name="descriptionAr" dir="rtl" rows={3} />
          </Field>
          <Field label={t("themes.descriptionEnOptional")} htmlFor="theme-desc-en">
            <TextArea id="theme-desc-en" name="descriptionEn" dir="ltr" rows={3} />
          </Field>
        </div>

        <Field label={t(keys.themes.niches)} help={t(keys.themes.nichesPlaceholder)}>
          <div role="group" aria-label={t(keys.themes.niches)} style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {THEME_NICHES.map((value) => (
              <FilterChip key={value} active={niches.includes(value)} onClick={() => toggleNiche(value)}>
                {t(keys.themes.nicheLabels[value])}
              </FilterChip>
            ))}
          </div>
        </Field>

        <Field
          label={t(keys.themes.sortWeight)}
          htmlFor="theme-sort-weight"
          help={t("themes.sortWeightHelp")}
          error={fieldErrors.sortWeight}
        >
          <Input
            id="theme-sort-weight"
            name="sortWeight"
            type="number"
            inputMode="numeric"
            dir="ltr"
            placeholder="0"
            invalid={Boolean(fieldErrors.sortWeight)}
            style={{ width: 120 }}
          />
        </Field>
      </form>
    </Drawer>
  );
}
